// SEO 元信息：与圣经译本覆盖的语言一一对应（27 种）。
// 说明：Google 主要使用 <title> 与页面正文，Bing/百度等会参考 meta keywords；
// 本表同时用于 JS 渲染后按界面语言动态替换 <title>/description/keywords。
// 译文由机器翻译起草，正式对外前建议请母语者复核。

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string[];
}

const SEO_META: Record<string, SeoMeta> = {
  "zh-CN": {
    title: "读经一年一遍 | 365天读经计划 · 每日打卡 · 经文分享",
    description:
      "读经一年一遍（365天读经计划）：每天按进度阅读新旧约经文，支持打卡记录、读后分享与经文美图生成；可对照多种译本，界面支持 50 多种语言。今天就开始，一年读完整本圣经。",
    keywords: [
      "读经一年一遍",
      "一年读经计划",
      "365天读经计划",
      "每日读经",
      "圣经打卡",
      "读经打卡",
      "读经分享",
      "经文分享",
    ],
  },
  "zh-TW": {
    title: "讀經一年一遍 | 365天讀經計畫 · 每日打卡 · 經文分享",
    description:
      "讀經一年一遍（365天讀經計畫）：每天按進度閱讀新舊約經文，支援打卡記錄、讀後分享與經文美圖生成；可對照多種譯本，介面支援 50 多種語言。今天就開始，一年讀完整本聖經。",
    keywords: ["讀經一年一遍", "一年讀經計畫", "每日讀經", "讀經打卡", "讀經分享", "經文分享"],
  },
  en: {
    title: "Read the Bible in One Year | 365-Day Bible Reading Plan with Daily Check-in & Sharing",
    description:
      "Read the Bible in One Year: a free 365-day plan through the whole Bible, with daily check-in, reading sharing and verse-card creation. Compare many translations and use the app in 50+ languages.",
    keywords: [
      "Read the Bible in One Year",
      "Bible in a Year",
      "One Year Bible Reading Plan",
      "Daily Bible Reading",
      "Bible check-in",
      "Bible verse sharing",
    ],
  },
  cs: {
    title: "Přečtěte si Bibli za jeden rok | 365denní plán čtení Bible s každodenním odškrtáváním a sdílením",
    description:
      "Přečtěte si celou Bibli za jeden rok: bezplatný 365denní plán, který vás provede celou Biblí, s denním odškrtáváním, sdílením čtení a tvorbou obrázků s verši. Porovnejte mnoho překladů a používejte aplikaci ve více než 50 jazycích.",
    keywords: ["Přečíst Bibli za jeden rok", "roční plán čtení Bible", "denní čtení Bible", "Biblický plán čtení"],
  },
  da: {
    title: "Læs hele Bibelen på ét år | 365-dages bibelplan med daglig check-in og deling",
    description:
      "Læs Bibelen på ét år: en gratis 365-dages plan gennem hele Bibelen med daglig check-in, deling af dine læsninger og generering af verskort. Sammenlign flere oversættelser, og brug appen på over 50 sprog.",
    keywords: ["læs Bibelen på ét år", "365-dages bibelplan", "daglig bibellæsning", "bibellæseplan"],
  },
  de: {
    title: "Bibel in einem Jahr lesen | 365-Tage-Bibelleseplan mit täglichem Check-in & Teilen",
    description:
      "Lies die Bibel in einem Jahr: ein kostenloser 365-Tage-Plan durch die ganze Bibel mit täglichem Check-in, Austausch über deine Lektüre und Erstellung von Versbildern. Vergleiche viele Übersetzungen – die App gibt es in über 50 Sprachen.",
    keywords: ["Bibel in einem Jahr lesen", "Bibelleseplan", "tägliche Bibellese", "Bibelvers teilen"],
  },
  el: {
    title: "Διαβάστε την Αγία Γραφή σε ένα χρόνο | Πρόγραμμα 365 ημερών με καθημερινή σημείωση και κοινοποίηση",
    description:
      "Διαβάστε ολόκληρη την Αγία Γραφή σε ένα χρόνο: δωρεάν πρόγραμμα 365 ημερών με καθημερινά αναγνώσματα, σημείωση προόδου, κοινοποίηση και δημιουργία εικόνων εδαφίων. Συγκρίνετε πολλές μεταφράσεις και χρησιμοποιήστε την εφαρμογή σε 50+ γλώσσες.",
    keywords: ["Αγία Γραφή σε ένα χρόνο", "πρόγραμμα ανάγνωσης της Βίβλου", "καθημερινή ανάγνωση της Βίβλου"],
  },
  es: {
    title: "Lee la Biblia en un año | Plan de lectura bíblica de 365 días con registro diario y compartir",
    description:
      "Lee la Biblia en un año: un plan gratuito de 365 días por toda la Biblia, con registro diario (check-in), compartir tus lecturas y crear imágenes de versículos. Compara muchas traducciones y usa la app en más de 50 idiomas.",
    keywords: [
      "leer la Biblia en un año",
      "plan de lectura bíblica",
      "leer la Biblia en 365 días",
      "compartir versículos bíblicos",
    ],
  },
  fi: {
    title: "Lue Raamattu vuodessa | 365 päivän Raamatun lukusuunnitelma päivittäisellä merkinnällä ja jakamisella",
    description:
      "Lue koko Raamattu vuodessa: ilmainen 365 päivän suunnitelma, joka johdattaa läpi koko Raamatun päivittäisillä lukemisilla, kuittauksilla, jakamisella ja jae-kuvien luonnilla. Vertaa useita käännöksiä ja käytä sovellusta yli 50 kielellä.",
    keywords: ["lue Raamattu vuodessa", "Raamatun lukusuunnitelma", "päivittäinen Raamatun luku"],
  },
  fr: {
    title: "Lire la Bible en un an | Plan de lecture biblique de 365 jours avec pointage quotidien et partage",
    description:
      "Lire la Bible en un an : un plan gratuit de 365 jours pour parcourir toute la Bible, avec pointage quotidien, partage de vos lectures et création de cartes de versets. Comparez de nombreuses traductions et utilisez l'application en plus de 50 langues.",
    keywords: [
      "lire la Bible en un an",
      "plan de lecture de la Bible",
      "lecture biblique quotidienne",
      "partager des versets bibliques",
    ],
  },
  hu: {
    title: "Olvassa el a Bibliát egy év alatt | 365 napos bibliaolvasó terv napi bejelöléssel és megosztással",
    description:
      "Olvassa el a teljes Bibliát egy év alatt: ingyenes 365 napos terv a Biblia végigolvasásához, napi bejelöléssel, olvasmányok megosztásával és igevers-képek készítésével. Hasonlítson össze számos fordítást; az alkalmazás 50+ nyelven érhető el.",
    keywords: ["Biblia egy év alatt", "bibliaolvasó terv", "napi bibliaolvasás", "igevers megosztás"],
  },
  it: {
    title: "Leggi la Bibbia in un anno | Piano di lettura biblica di 365 giorni con check-in giornaliero e condivisione",
    description:
      "Leggi la Bibbia in un anno: un piano gratuito di 365 giorni attraverso tutta la Bibbia, con check-in giornaliero, condivisione delle letture e creazione di immagini dei versetti. Confronta molte traduzioni e usa l'app in oltre 50 lingue.",
    keywords: ["leggere la Bibbia in un anno", "piano di lettura della Bibbia", "lettura biblica quotidiana"],
  },
  ja: {
    title: "聖書を1年で読み通す | 365日読書プラン 毎日のチェックイン・シェア対応",
    description:
      "1年で聖書を読み通せる無料の365日プラン。毎日の旧約・新約聖書、チェックイン記録、読書シェア、聖句カード生成に対応。多数の翻訳を比較でき、50以上の言語で利用できます。",
    keywords: ["聖書を1年で読む", "一年聖書通読", "365日聖書プラン", "毎日の聖書通読", "聖句シェア"],
  },
  ko: {
    title: "1년 만에 성경 읽기 | 365일 성경 읽기 플랜 - 매일 체크인·공유",
    description:
      "1년 만에 성경 전체를 읽는 무료 365일 플랜입니다. 매일 구약·신약 본문, 체크인 기록, 읽은 말씀 공유, 성경 구절 카드 생성을 지원하며 여러 번역본을 비교하고 50개 이상의 언어로 사용할 수 있습니다.",
    keywords: ["성경 일년 일독", "1년 성경 읽기", "365일 성경 읽기 플랜", "매일 성경 읽기", "성경 구절 공유"],
  },
  lt: {
    title: "Perskaitykite Bibliją per metus | 365 dienų Biblijos skaitymo planas su kasdieniu pažymėjimu ir dalijimusi",
    description:
      "Perskaitykite visą Bibliją per metus: nemokamas 365 dienų planas per visą Bibliją su kasdieniu skaitymu, pažymėjimu, dalijimusi ir eilutės paveikslėlių kūrimu. Palyginkite daugybę vertimų ir naudokitės programėle 50+ kalbų.",
    keywords: ["Biblija per metus", "Biblijos skaitymo planas", "kasdienis Biblijos skaitymas"],
  },
  lv: {
    title: "Izlasi Bībeli gada laikā | 365 dienu Bībeles lasīšanas plāns ar ikdienas atzīmēšanu un kopīgošanu",
    description:
      "Izlasi visu Bībeli gada laikā: bezmaksas 365 dienu plāns, kas ved cauri visai Bībelei ar ikdienas lasījumiem, atzīmēšanu, kopīgošanu un pantu attēlu veidošanu. Salīdzini daudzus tulkojumus un lieto lietotni 50+ valodās.",
    keywords: ["izlasīt Bībeli gada laikā", "Bībeles lasīšanas plāns", "ikdienas Bībeles lasīšana"],
  },
  my: {
    title: "တစ်နှစ်အတွင်း သမ္မာကျမ်းစာဖတ်ရှုခြင်း | 365 ရက် ကျမ်းစာဖတ်ရှုမှု အစီအစဉ်",
    description:
      "သမ္မာကျမ်းစာတစ်အုပ်လုံးကို တစ်နှစ်အတွင်း ဖတ်ရှုနိုင်ရန် အခမဲ့ 365 ရက်ကြာ အစီအစဉ်။ နေ့စဉ် ဓမ္မဟောင်း/ဓမ္မသစ်ကျမ်းပိုဒ်များ၊ မှတ်သားခြင်း၊ ဝေမျှခြင်းနှင့် ကျမ်းချက်ပုံများ ပြုလုပ်ခြင်းတို့ကို ပံ့ပိုးသည်။",
    keywords: ["သမ္မာကျမ်းစာကို တစ်နှစ်အတွင်းဖတ်ရှုခြင်း", "365 ရက် ကျမ်းစာစီမံကိန်း", "နေ့စဉ်ကျမ်းစာဖတ်ရှုခြင်း"],
  },
  nl: {
    title: "Lees de Bijbel in één jaar | 365-dagen Bijbelleesplan met dagelijkse check-in en delen",
    description:
      "Lees de hele Bijbel in één jaar: een gratis 365-dagenplan door de hele Bijbel, met dagelijkse check-in, het delen van je lezingen en het maken van verskaarten. Vergelijk veel vertalingen en gebruik de app in meer dan 50 talen.",
    keywords: ["Bijbel in één jaar lezen", "Bijbelleesplan", "dagelijks Bijbellezen", "Bijbelverzen delen"],
  },
  no: {
    title: "Les hele Bibelen på ett år | 365-dagers bibelplan med daglig innsjekking og deling",
    description:
      "Les Bibelen på ett år: en gratis 365-dagers plan gjennom hele Bibelen, med daglig innsjekking, deling av lesningen din og generering av verskort. Sammenlign mange oversettelser og bruk appen på over 50 språk.",
    keywords: ["lese Bibelen på ett år", "bibelplan", "daglig bibellesning", "lese hele Bibelen"],
  },
  pl: {
    title: "Przeczytaj Biblię w jeden rok | 365-dniowy plan czytania Biblii z codziennym odhaczaniem i dzieleniem się",
    description:
      "Przeczytaj całą Biblię w jeden rok: darmowy 365-dniowy plan prowadzący przez całą Biblię, z codziennym odhaczaniem, dzieleniem się czytaniem i tworzeniem obrazków z wersetami. Porównuj wiele tłumaczeń i korzystaj z aplikacji w ponad 50 językach.",
    keywords: ["czytanie Biblii w rok", "plan czytania Biblii", "codzienne czytanie Biblii", "dzielenie się wersetami"],
  },
  pt: {
    title: "Leia a Bíblia em um ano | Plano de leitura bíblica de 365 dias com check-in diário e compartilhamento",
    description:
      "Leia a Bíblia inteira em um ano: um plano gratuito de 365 dias por toda a Bíblia, com check-in diário, compartilhamento da leitura e criação de cartões de versículos. Compare muitas traduções e use o app em mais de 50 idiomas.",
    keywords: [
      "ler a Bíblia em um ano",
      "plano de leitura bíblica",
      "leitura bíblica diária",
      "compartilhar versículos bíblicos",
    ],
  },
  ru: {
    title: "Прочитайте Библию за год | План чтения Библии на 365 дней с ежедневной отметкой и обменом",
    description:
      "Прочитайте всю Библию за один год: бесплатный план на 365 дней, который проведёт вас по всей Библии с ежедневными чтениями, отметками о прочтении, обменом впечатлениями и созданием карточек со стихами. Сравнивайте переводы — приложение доступно на 50+ языках.",
    keywords: ["прочитать Библию за год", "план чтения Библии", "ежедневное чтение Библии", "обмен стихами из Библии"],
  },
  sl: {
    title: "Preberite Sveto pismo v enem letu | 365-dnevni načrt branja Svetega pisma z dnevnim označevanjem in deljenjem",
    description:
      "Preberite celotno Sveto pismo v enem letu: brezplačen 365-dnevni načrt skozi vse Sveto pismo z dnevnim branjem, označevanjem, deljenjem in ustvarjanjem slik z vrsticami. Primerjajte številne prevode; aplikacija je na voljo v več kot 50 jezikih.",
    keywords: ["Sveto pismo v enem letu", "načrt branja Svetega pisma", "dnevno branje Svetega pisma"],
  },
  sq: {
    title: "Lexoni Biblën në një vit | Plan 365-ditor i leximit të Biblës me regjistrim dhe ndarje ditore",
    description:
      "Lexoni të gjithë Biblën në një vit: plan falas 365-ditor përmes gjithë Biblës, me lexim ditor, regjistrim (check-in), ndarje të leximeve dhe krijim kartelash me vargje. Krahasoni shumë përkthime dhe përdorni aplikacionin në mbi 50 gjuhë.",
    keywords: ["lexoni Biblën në një vit", "plan leximi i Biblës", "lexim ditor i Biblës"],
  },
  sr: {
    title: "Прочитајте Библију за годину дана | План читања Библије на 365 дана са свакодневном пријавом и дељењем",
    description:
      "Прочитајте целу Библију за годину дана: бесплатан план на 365 дана кроз целу Библију, са свакодневним читањима, пријавом (check-in), дељењем утисака и прављењем слика са стиховима. Упоредите много превода и користите апликацију на 50+ језика.",
    keywords: ["читање Библије за годину дана", "план читања Библије", "свакодневно читање Библије"],
  },
  sv: {
    title: "Läs Bibeln på ett år | 365-dagars bibelplan med daglig incheckning och delning",
    description:
      "Läs hela Bibeln på ett år: en kostnadsfri 365-dagarsplan genom hela Bibeln med daglig läsning, incheckning, delning och skapande av verskort. Jämför många översättningar och använd appen på över 50 språk.",
    keywords: ["läsa Bibeln på ett år", "bibelplan", "daglig bibelläsning", "dela bibelverser"],
  },
  vi: {
    title: "Đọc trọn Kinh Thánh trong một năm | Kế hoạch đọc Kinh Thánh 365 ngày với điểm danh và chia sẻ hằng ngày",
    description:
      "Đọc trọn Kinh Thánh trong một năm: kế hoạch 365 ngày miễn phí xuyên suốt toàn bộ Kinh Thánh, với bài đọc hằng ngày, điểm danh, chia sẻ cảm nhận và tạo thẻ câu Kinh Thánh. So sánh nhiều bản dịch và dùng ứng dụng bằng hơn 50 ngôn ngữ.",
    keywords: ["đọc Kinh Thánh trong một năm", "kế hoạch đọc Kinh Thánh", "đọc Kinh Thánh mỗi ngày", "chia sẻ câu Kinh Thánh"],
  },
};

const LOCALE_ALIAS: Record<string, string> = {
  "en-AU": "en",
  "en-CA": "en",
  "en-GB": "en",
  "pt-BR": "pt",
  zh: "zh-CN",
  "zh-SG": "zh-CN",
  "zh-HK": "zh-TW",
  "zh-MO": "zh-TW",
};

/** 取某界面 locale 对应的 SEO 元信息；找不到精确匹配时回退到主语言，最后回退英文。 */
export const seoForLocale = (locale: string): SeoMeta => {
  const exact = SEO_META[locale];
  if (exact) return exact;
  const alias = LOCALE_ALIAS[locale];
  if (alias) return SEO_META[alias];
  const base = (locale || "").toLowerCase().split("-")[0];
  if (SEO_META[base]) return SEO_META[base];
  return SEO_META.en;
};

export const seoLocales = Object.keys(SEO_META);